// src/components/Auth.js
import React, { useState } from 'react';
import { auth, db } from '../firebaseConfig';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { ToastContainer, toast } from 'react-toastify';

import { FaEnvelope, FaEye, FaEyeSlash, FaLock } from 'react-icons/fa';
import '../App.css';


function Auth({ startRegistering, initialMessage }) {
  const [isRegistering, setIsRegistering] = useState(startRegistering);
  const [message, setMessage] = useState(initialMessage || '');
  const [isError, setIsError] = useState(false);

  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthPlace, setBirthPlace] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [phone, setPhone] = useState('');

  const [userType, setUserType] = useState('cittadino');

  const [entityRole, setEntityRole] = useState('legale_rappresentante');
  const [entityName, setEntityName] = useState('');
  const [entityAddress, setEntityAddress] = useState('');
  const [entityCF, setEntityCF] = useState('');
  const [entityEmail, setEntityEmail] = useState('');
  const [legalRepName, setLegalRepName] = useState('');
  const [legalRepPhone, setLegalRepPhone] = useState('');

  const showEntityFields = userType === 'associazione' || userType === 'istituzione';

  const handlePhoneChange = (e) => {
    const inputValue = e.target.value;
    const isNumeric = /^[0-9]*$/.test(inputValue);

    if (isNumeric) {
      setPhone(inputValue);
    } else {
      toast.error("Inserire solo numeri nel campo cellulare!");
    }
  };

  const handleLegalRepPhoneChange = (e) => {
    const inputValue = e.target.value;
    if (/^[0-9]*$/.test(inputValue)) {
      setLegalRepPhone(inputValue);
    } else {
      toast.error("Inserire solo numeri nel cellulare del rappresentante!");
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setIsError(true);
      setMessage("Inserisci la tua email nel campo sopra per resettare la password.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setIsError(false);
      setMessage("Ti abbiamo inviato una mail per resettare la password. Controlla la posta! ⚠️ Verifica anche nella cartella SPAM.");
    } catch (error) {
      setIsError(true);
      if (error.code === 'auth/user-not-found') setMessage("Nessun account trovato con questa email.");
      else if (error.code === 'auth/invalid-email') setMessage("Email non valida.");
      else setMessage("Errore: " + error.message);
    }
  };

  const handleAuth = async () => {
    setMessage("");
    setIsError(false);
    const cleanEmail = email.trim();

    try {
      if (isRegistering) {
        // --- REGISTRAZIONE ---
        if (password !== confirmPassword) { setIsError(true); setMessage("Le due password non coincidono."); return; }
        if (password.length < 6) { setIsError(true); setMessage("La password deve essere di almeno 6 caratteri."); return; }
        if (!firstName || !lastName || !phone || !birthDate) { setIsError(true); setMessage("Compila tutti i campi obbligatori personali."); return; }
        if (showEntityFields && (!entityName || !entityCF)) { setIsError(true); setMessage("Compila i dati obbligatori dell'ente."); return; }

        const userCredential = await createUserWithEmailAndPassword(auth, cleanEmail, password);
        const user = userCredential.user;

        let userData = {
          email: cleanEmail, firstName, lastName, birthPlace, birthDate, phone, userType,
          role: "utente", createdAt: new Date(), isDisabled: false
        };

        if (showEntityFields) {
          // --- AUTOMATISMO LEGALE RAPPRESENTANTE ---
          // Se l'utente è "legale_rappresentante", usiamo i SUOI dati personali (Nome, Cognome, Cellulare).
          // Se è un altro ruolo (es. operatore), usiamo i dati specifici inseriti nei campi appositi.

          const finalLegalRepName = entityRole === 'legale_rappresentante'
            ? `${firstName} ${lastName}`
            : legalRepName;

          const finalLegalRepPhone = entityRole === 'legale_rappresentante'
            ? phone
            : legalRepPhone;

          userData = {
            ...userData,
            entityData: {
              roleInEntity: entityRole,
              name: entityName,
              address: entityAddress,
              cf: entityCF,
              email: entityEmail,
              legalRepName: finalLegalRepName,
              legalRepPhone: finalLegalRepPhone
            }
          };
        }

        await setDoc(doc(db, "users", user.uid), userData);
        await sendEmailVerification(user);
        await signOut(auth);
        setRegistrationSuccess(true);

      } else {
        // --- LOGIN ---
        const userCredential = await signInWithEmailAndPassword(auth, cleanEmail, password);
        const user = userCredential.user;
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          if (userData.isDisabled) {
            await signOut(auth);
            throw new Error("ACCOUNT_BLOCKED_BY_ADMIN");
          }
        }

        if (!user.emailVerified) {
          await signOut(auth);
          setIsError(true);
          setMessage("ATTENZIONE: La tua email non è stata verificata. Controlla la posta (anche spam) e clicca sul link.");
          return;
        }
      }

    } catch (error) {
      setIsError(true);
      console.error("Errore Auth:", error.code || error.message);

      if (error.message === "ACCOUNT_BLOCKED_BY_ADMIN") {
        setMessage("ACCESSO NEGATO: Il tuo account è stato disabilitato dall'amministratore.");
      }
      else if (error.code === 'auth/wrong-password') setMessage("Password errata.");
      else if (error.code === 'auth/email-already-in-use') setMessage("Email già registrata.");
      else if (error.code === 'auth/user-not-found') setMessage("Utente non trovato.");
      else if (error.code === 'auth/invalid-email') setMessage("Formato email non valido.");
      else if (error.code === 'auth/invalid-credential') setMessage("Email o password errati.");
      else setMessage("Errore: " + error.message);
    }
  };

  if (registrationSuccess) {
    return (
      <div style={{ textAlign: 'center', width: '100%', padding: '20px' }}>
        <FaEnvelope size={60} color="#007bff" style={{ marginBottom: '20px' }} />
        <h2 style={{ color: '#333', marginBottom: '15px' }}>Controlla la tua Posta!</h2>
        <p style={{ fontSize: '16px', lineHeight: '1.5', color: '#555', marginBottom: '30px' }}>
          Abbiamo inviato un'email di conferma a <strong>{email}</strong>.<br />
          Per attivare il tuo account, devi cliccare sul link contenuto nella mail.
        </p>
        <div style={{ background: '#fff3cd', padding: '10px', borderRadius: '8px', fontSize: '14px', color: '#856404', marginBottom: '30px' }}>
          ⚠️ <strong>Non trovi la mail?</strong> Controlla nella cartella <strong>SPAM</strong> o <strong>Posta Indesiderata</strong>.
        </div>
        <button className="primary-button" onClick={() => { setRegistrationSuccess(false); setIsRegistering(false); setMessage(""); }}>
          Torna al Login
        </button>
      </div>
    );
  }

  const selectStyle = { width: '100%', padding: '12px 15px', marginBottom: '15px', border: '2px solid rgba(255, 255, 255, 0.5)', background: 'rgba(255, 255, 255, 0.9)', borderRadius: '12px', fontFamily: 'Poppins, sans-serif', fontSize: '15px', outline: 'none' };

  return (
    <div style={{ textAlign: 'center', width: '100%' }}>
      <ToastContainer position="top-center" autoClose={3000} />

      <h2 style={{ marginBottom: '10px', color: '#333' }}>{isRegistering ? "Nuova Registrazione" : "Accedi"}</h2>
      <div style={{ width: '100%' }}>

        {isRegistering && (
          <>
            <h4 style={{ textAlign: 'left', margin: '10px 0', color: '#555' }}>Dati Personali</h4>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <input className="modern-input" style={{ flex: 1, minWidth: '200px' }} type="text" placeholder="Nome" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              <input className="modern-input" style={{ flex: 1, minWidth: '200px' }} type="text" placeholder="Cognome" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <input className="modern-input" style={{ flex: 1, minWidth: '200px' }} type="text" placeholder="Luogo di Nascita" value={birthPlace} onChange={(e) => setBirthPlace(e.target.value)} />
              <input className="modern-input" style={{ flex: 1, minWidth: '200px' }} type="date" placeholder="Data di Nascita" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
            </div>

            <input
              className="modern-input"
              type="tel"
              placeholder="Tuo Cellulare"
              value={phone}
              onChange={handlePhoneChange}
            />

            <h4 style={{ textAlign: 'left', margin: '10px 0', color: '#555' }}>Tipologia</h4>
            <select style={selectStyle} value={userType} onChange={(e) => setUserType(e.target.value)}>
              <option value="cittadino">Cittadino Privato</option>
              <option value="gruppo_informale">Gruppo Informale</option>
              <option value="associazione">Associazione</option>
              <option value="istituzione">Istituzione</option>
            </select>

            {showEntityFields && (
              <div style={{ background: '#f9f9f9', padding: '15px', borderRadius: '10px', marginBottom: '15px', border: '1px solid #eee' }}>
                <h4 style={{ textAlign: 'left', margin: '0 0 10px 0', color: '#007bff' }}>Dati dell'Ente</h4>
                <label style={{ display: 'block', textAlign: 'left', fontSize: '12px', color: '#666' }}>La tua posizione:</label>
                <select style={selectStyle} value={entityRole} onChange={(e) => setEntityRole(e.target.value)}>
                  <option value="legale_rappresentante">Legale Rappresentante</option>
                  <option value="dirigente">Dirigente</option>
                  <option value="operatore">Operatore</option>
                  <option value="volontario">Volontario</option>
                </select>
                <input className="modern-input" type="text" placeholder="Nome Ente" value={entityName} onChange={(e) => setEntityName(e.target.value)} />
                <input className="modern-input" type="text" placeholder="Indirizzo Ente" value={entityAddress} onChange={(e) => setEntityAddress(e.target.value)} />
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <input className="modern-input" style={{ flex: 1, minWidth: '200px' }} type="text" placeholder="CF Ente" value={entityCF} onChange={(e) => setEntityCF(e.target.value)} />
                  <input className="modern-input" style={{ flex: 1, minWidth: '200px' }} type="email" placeholder="Email Ente" value={entityEmail} onChange={(e) => setEntityEmail(e.target.value)} />
                </div>

                {/* --- SEZIONE LEGALE RAPPRESENTANTE CONDIZIONALE --- */}
                {/* Se l'utente è Legale Rappresentante, NON mostriamo questi campi perché usiamo i suoi dati personali */}
                {entityRole !== 'legale_rappresentante' && (
                  <>
                    <h5 style={{ textAlign: 'left', margin: '10px 0 5px 0', color: '#555' }}>Dati Legale Rappresentante</h5>
                    <input
                      className="modern-input"
                      type="text"
                      placeholder="Nome e Cognome Rap. Legale"
                      value={legalRepName}
                      onChange={(e) => setLegalRepName(e.target.value)}
                    />
                    <input
                      className="modern-input"
                      type="tel"
                      placeholder="Cellulare Rap. Legale"
                      value={legalRepPhone}
                      onChange={handleLegalRepPhoneChange}
                    />
                  </>
                )}

                {entityRole === 'legale_rappresentante' && (
                  <div style={{ fontSize: '12px', color: '#28a745', textAlign: 'left', marginTop: '10px', fontStyle: 'italic' }}>
                    ✓ Essendo il Legale Rappresentante, verranno utilizzati il tuo Nome, Cognome e Cellulare inseriti sopra.
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {isRegistering && <h4 style={{ textAlign: 'left', margin: '10px 0', color: '#555' }}>Credenziali di Accesso</h4>}

        <input className="modern-input" type="email" placeholder="Email (Login)" value={email} onChange={(e) => setEmail(e.target.value)} />

        <div style={{ position: 'relative' }}>
          <input
            className="modern-input"
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ paddingRight: '40px' }}
          />
          <div onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '15px', top: '20px', transform: 'translateY(-20%)', cursor: 'pointer', color: '#666', zIndex: 5 }}>
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </div>
        </div>

        {!isRegistering && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px' }}>
            <button
              onClick={handlePasswordReset}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#444',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.3s ease',
                textDecoration: 'none',
                padding: '8px 12px',
                borderRadius: '20px',
                marginTop: '5px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#007bff';
                e.currentTarget.style.backgroundColor = 'rgba(0, 123, 255, 0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 2px 5px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#444';
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <FaLock size={13} /> Password dimenticata?
            </button>
          </div>
        )}

        {isRegistering && (
          <input className="modern-input" type="password" placeholder="Conferma Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} style={{ border: isError && password !== confirmPassword ? '2px solid red' : '' }} />
        )}

        <button className="primary-button" onClick={handleAuth} style={{ marginTop: '10px' }}>
          {isRegistering ? "Conferma Registrazione" : "Accedi"}
        </button>
      </div>

      {message && (
        <div style={{
          marginTop: '20px', padding: '10px', borderRadius: '8px',
          backgroundColor: isError ? '#ffebee' : (message.includes("prenotare") ? '#e3f2fd' : '#d4edda'),
          color: isError ? '#c62828' : (message.includes("prenotare") ? '#0d47a1' : '#155724'),
          fontSize: '14px', fontWeight: '500'
        }}>
          {message}
        </div>
      )}

      <div style={{ fontSize: '14px', color: '#555', marginTop: '25px' }}>
        {isRegistering ? "Hai già un account?" : "Non hai ancora un account?"} <br />
        <span
          onClick={() => { setIsRegistering(!isRegistering); setMessage(""); setIsError(false); setConfirmPassword(''); }}
          className="toggle-btn"
          style={{ display: 'inline-block', marginTop: '10px' }}
        >
          {isRegistering ? "Accedi al tuo profilo" : "Registrati ora"}
        </span>
      </div>

    </div>
  );
}

export default Auth;